import React, { ElementType } from 'react';
import {
  Badge,
  Box,
  Button,
  Flex,
  HStack,
  IconButton,
  Input,
  InputGroup,
  InputLeftElement,
  Select,
  SimpleGrid,
  Skeleton,
  SkeletonText,
  Stack,
  Text,
  VStack,
} from '@chakra-ui/react';
import { Building2, LayoutGrid, Rows3, Search, SlidersHorizontal } from 'lucide-react';

export const companyAccent = '#0F766E';
export const companyAccentSoft = '#E6FFFA';
export const companyBorder = 'gray.200';

export const CompanyShell = ({
  children,
  compact = false,
}: {
  children: React.ReactNode;
  compact?: boolean;
}) => (
  <Box
    px={{ base: 4, md: 6, xl: 8 }}
    py={{ base: 4, md: 6 }}
    bg="linear-gradient(180deg, #f3fbfa 0%, #ffffff 34%)"
    minH="calc(100vh - 72px)"
  >
    <Box maxW={compact ? '1120px' : '1360px'} mx="auto">
      {children}
    </Box>
  </Box>
);

export const CompanyHero = ({
  title,
  description,
  primaryAction,
  secondaryAction,
  eyebrow = 'Companies',
}: {
  title: string;
  description: string;
  primaryAction?: React.ReactNode;
  secondaryAction?: React.ReactNode;
  eyebrow?: string;
}) => (
  <Box
    bg="white"
    borderWidth="1px"
    borderColor={companyBorder}
    borderRadius={{ base: '24px', md: '28px' }}
    px={{ base: 5, md: 7 }}
    py={{ base: 5, md: 7 }}
    boxShadow="0 18px 48px rgba(15, 23, 42, 0.06)"
    mb={6}
  >
    <Flex
      align={{ base: 'stretch', lg: 'center' }}
      justify="space-between"
      direction={{ base: 'column', lg: 'row' }}
      gap={5}
    >
      <VStack align="flex-start" spacing={3} maxW="3xl">
        <Text
          fontSize="xs"
          textTransform="uppercase"
          letterSpacing="0.18em"
          fontWeight="700"
          color={companyAccent}
        >
          {eyebrow}
        </Text>
        <Text
          as="h1"
          fontSize={{ base: '2xl', md: '4xl' }}
          fontWeight="800"
          color="gray.900"
          lineHeight={{ base: '1.2', md: '1.05' }}
          letterSpacing="-0.03em"
        >
          {title}
        </Text>
        <Text fontSize={{ base: 'sm', md: 'md' }} color="gray.600" maxW="2xl">
          {description}
        </Text>
      </VStack>
      {(primaryAction || secondaryAction) && (
        <Stack direction={{ base: 'column', sm: 'row' }} spacing={3}>
          {primaryAction}
          {secondaryAction}
        </Stack>
      )}
    </Flex>
  </Box>
);

export const CompanyPrimaryButton = ({
  children,
  ...props
}: React.ComponentProps<typeof Button>) => (
  <Button
    bg={companyAccent}
    color="white"
    leftIcon={props.leftIcon}
    _hover={{ bg: '#0B5F59', transform: 'translateY(-1px)' }}
    _active={{ bg: '#084A45' }}
    boxShadow="0 12px 26px rgba(15, 118, 110, 0.18)"
    borderRadius="16px"
    fontWeight="700"
    px={5}
    {...props}
  >
    {children}
  </Button>
);

export const CompanySectionCard = ({
  title,
  description,
  action,
  children,
}: {
  title: string;
  description?: string;
  action?: React.ReactNode;
  children: React.ReactNode;
}) => (
  <Box
    bg="white"
    borderWidth="1px"
    borderColor={companyBorder}
    borderRadius="24px"
    px={{ base: 4, md: 6 }}
    py={{ base: 4, md: 5 }}
    boxShadow="0 12px 32px rgba(15, 23, 42, 0.04)"
  >
    <Flex
      justify="space-between"
      align={{ base: 'flex-start', md: 'center' }}
      direction={{ base: 'column', md: 'row' }}
      gap={3}
      mb={5}
    >
      <Box>
        <Text fontSize={{ base: 'lg', md: 'xl' }} fontWeight="800" color="gray.900">
          {title}
        </Text>
        {description ? (
          <Text fontSize="sm" color="gray.500" mt={1}>
            {description}
          </Text>
        ) : null}
      </Box>
      {action}
    </Flex>
    {children}
  </Box>
);

export const CompanyMetricStrip = ({
  items,
}: {
  items: Array<{ label: string; value: string; hint?: string; onClick?: () => void; active?: boolean }>;
}) => (
  <SimpleGrid columns={{ base: 2, lg: 4 }} spacing={4} mb={6}>
    {items.map((item) => (
      <Box
        key={item.label}
        bg={item.active ? companyAccentSoft : 'white'}
        borderWidth="1px"
        borderColor={item.active ? 'teal.200' : companyBorder}
        borderRadius="22px"
        px={4}
        py={4}
        cursor={item.onClick ? 'pointer' : 'default'}
        onClick={item.onClick}
        transition="all 0.16s ease"
        _hover={
          item.onClick
            ? {
                borderColor: 'teal.300',
                transform: 'translateY(-1px)',
                boxShadow: '0 10px 24px rgba(15, 23, 42, 0.05)',
              }
            : undefined
        }
      >
        <Text fontSize="xs" color="gray.500" textTransform="uppercase" letterSpacing="0.08em">
          {item.label}
        </Text>
        <Text fontSize={{ base: 'xl', md: '2xl' }} fontWeight="800" color="gray.900" mt={2}>
          {item.value}
        </Text>
        {item.hint ? (
          <Text fontSize="sm" color="gray.500" mt={1}>
            {item.hint}
          </Text>
        ) : null}
      </Box>
    ))}
  </SimpleGrid>
);

export const CompanyCommandBar = ({
  search,
  onSearchChange,
  inputRef,
  filter,
  onFilterChange,
  sort,
  onSortChange,
  view,
  onViewChange,
  resultText,
}: {
  search: string;
  onSearchChange: (value: string) => void;
  inputRef?: React.RefObject<HTMLInputElement | null>;
  filter: string;
  onFilterChange: (value: string) => void;
  sort: string;
  onSortChange: (value: string) => void;
  view: 'table' | 'cards';
  onViewChange: (view: 'table' | 'cards') => void;
  resultText: string;
}) => (
  <Stack
    spacing={4}
    bg="rgba(255,255,255,0.9)"
    borderWidth="1px"
    borderColor={companyBorder}
    borderRadius="24px"
    p={{ base: 4, md: 5 }}
    mb={5}
    position="sticky"
    top={{ base: '72px', md: '84px' }}
    zIndex={5}
    backdropFilter="blur(14px)"
  >
    <Flex
      direction={{ base: 'column', xl: 'row' }}
      justify="space-between"
      align={{ base: 'stretch', xl: 'center' }}
      gap={3}
    >
      <InputGroup maxW={{ xl: '420px' }}>
        <InputLeftElement pointerEvents="none">
          <Search size={16} color="#718096" />
        </InputLeftElement>
        <Input
          ref={inputRef}
          value={search}
          onChange={(event) => onSearchChange(event.target.value)}
          placeholder="Search by company, website, email, or phone"
          borderRadius="16px"
          bg="gray.50"
          borderColor="gray.200"
          _focusVisible={{
            borderColor: 'teal.300',
            boxShadow: '0 0 0 3px rgba(45, 212, 191, 0.18)',
            bg: 'white',
          }}
        />
      </InputGroup>

      <Flex
        gap={3}
        align={{ base: 'stretch', md: 'center' }}
        wrap="wrap"
        justify={{ base: 'stretch', xl: 'flex-end' }}
      >
        <InputGroup maxW={{ base: 'full', md: '220px' }}>
          <InputLeftElement pointerEvents="none">
            <SlidersHorizontal size={16} color="#718096" />
          </InputLeftElement>
          <Select
            value={filter}
            onChange={(event) => onFilterChange(event.target.value)}
            borderRadius="16px"
            bg="gray.50"
            pl={8}
          >
            <option value="all">All companies</option>
            <option value="complete">Complete profiles</option>
            <option value="missing-contact">Missing contact info</option>
            <option value="missing-website">Missing website</option>
          </Select>
        </InputGroup>

        <Select
          value={sort}
          onChange={(event) => onSortChange(event.target.value)}
          borderRadius="16px"
          bg="gray.50"
          maxW={{ base: 'full', md: '180px' }}
        >
          <option value="name-asc">Name A-Z</option>
          <option value="name-desc">Name Z-A</option>
          <option value="website-first">Website first</option>
          <option value="contact-first">Contact first</option>
        </Select>

        <HStack spacing={1} borderWidth="1px" borderColor="gray.200" borderRadius="16px" p={1}>
          <IconButton
            aria-label="Table view"
            icon={<Rows3 size={16} />}
            size="sm"
            variant={view === 'table' ? 'solid' : 'ghost'}
            bg={view === 'table' ? companyAccent : 'transparent'}
            color={view === 'table' ? 'white' : 'gray.600'}
            _hover={view === 'table' ? { bg: companyAccent } : { bg: 'gray.100' }}
            onClick={() => onViewChange('table')}
          />
          <IconButton
            aria-label="Card view"
            icon={<LayoutGrid size={16} />}
            size="sm"
            variant={view === 'cards' ? 'solid' : 'ghost'}
            bg={view === 'cards' ? companyAccent : 'transparent'}
            color={view === 'cards' ? 'white' : 'gray.600'}
            _hover={view === 'cards' ? { bg: companyAccent } : { bg: 'gray.100' }}
            onClick={() => onViewChange('cards')}
          />
        </HStack>
      </Flex>
    </Flex>
    <Text fontSize="sm" color="gray.500">
      {resultText}
    </Text>
  </Stack>
);

export const CompanyField = ({
  label,
  value,
}: {
  label: string;
  value?: React.ReactNode;
}) => (
  <Box>
    <Text fontSize="xs" textTransform="uppercase" letterSpacing="0.08em" color="gray.500" mb={1}>
      {label}
    </Text>
    <Text color="gray.900" fontWeight="600">
      {value || 'N/A'}
    </Text>
  </Box>
);

export const CompanyStatusBadge = ({
  complete,
}: {
  complete: boolean;
}) => (
  <Badge
    borderRadius="full"
    px={3}
    py={1}
    colorScheme={complete ? 'teal' : 'orange'}
  >
    {complete ? 'Complete profile' : 'Needs details'}
  </Badge>
);

export const CompanyEmptyState = ({
  title,
  description,
  action,
}: {
  title: string;
  description: string;
  action?: React.ReactNode;
}) => (
  <VStack
    bg="white"
    borderWidth="1px"
    borderColor={companyBorder}
    borderRadius="28px"
    px={{ base: 6, md: 10 }}
    py={{ base: 10, md: 14 }}
    spacing={4}
    textAlign="center"
    boxShadow="0 12px 32px rgba(15, 23, 42, 0.04)"
  >
    <Box
      w="64px"
      h="64px"
      borderRadius="20px"
      bg={companyAccentSoft}
      display="flex"
      alignItems="center"
      justifyContent="center"
    >
      <Box as={Building2 as ElementType} boxSize={7} color={companyAccent} />
    </Box>
    <VStack spacing={2}>
      <Text fontSize="xl" fontWeight="800" color="gray.900">
        {title}
      </Text>
      <Text fontSize="sm" color="gray.500" maxW="md">
        {description}
      </Text>
    </VStack>
    {action}
  </VStack>
);

export const CompanyDashboardSkeleton = () => (
  <Stack spacing={4}>
    <SimpleGrid columns={{ base: 2, lg: 4 }} spacing={4}>
      {[...Array(4)].map((_, idx) => (
        <Box key={idx} bg="white" borderWidth="1px" borderColor={companyBorder} borderRadius="22px" px={4} py={4}>
          <Skeleton h="12px" w="40%" mb={3} />
          <Skeleton h="24px" w="55%" mb={2} />
          <Skeleton h="12px" w="70%" />
        </Box>
      ))}
    </SimpleGrid>
    {[...Array(5)].map((_, idx) => (
      <Box key={idx} bg="white" borderWidth="1px" borderColor={companyBorder} borderRadius="22px" px={5} py={4}>
        <Skeleton h="18px" w="30%" mb={3} />
        <SkeletonText noOfLines={2} spacing="3" />
      </Box>
    ))}
  </Stack>
);
